/**
 * The things you shoot at: two drones and six balloons, and what they do about being shot at.
 *
 * ## Where a target actually lives
 *
 * Not in sim-frame NED. A target's authoritative position is an offset in its **course's** local
 * frame, and the course is anchored to a latitude and longitude; the sim-frame position is derived
 * once a tick by adding the course's current anchor. That is not indirection for its own sake — it
 * is what makes the whole thing survive a floating-origin rebase for free. Store NED directly and
 * every target teleports up to 500 m sideways the first time the aircraft crosses a rebase
 * boundary, and the fix is a per-target geodetic round trip that this design simply never needs.
 *
 * The argument got *stronger* when `ORIGIN_REBASE_DISTANCE_M` came down from 10 km to 500 m, which
 * is worth saying because the number in it got smaller: the teleport shrank, but it now happens
 * every 1.9 s at 500 kt instead of every 39 s. Ordnance, which has no geodetic identity to derive
 * from, is the half of the combat system that has to be carried by hand — see
 * `Cannon.applyOriginShift`.
 *
 * Over the few kilometres a course spans, the anchor's north/east axes and the origin's are
 * parallel to well under a metre, so "add the anchor" really is the whole conversion.
 *
 * ## The drones
 *
 * **The racetrack drone** flies a stadium circuit — two straights joined by two semicircles — at
 * a constant speed and height. It is parameterised by arc length rather than by integrating a
 * heading, so it is exactly periodic and cannot drift: the same seed puts it in the same place on
 * the same tick for ever, which is what a scored, repeatable gunnery run needs.
 *
 * **The evasive drone** flies the same circuit until something threatens it, and then it does not.
 * It has three states and they escalate: it *jinks* when a seeker is on it, and it *breaks* — one
 * hard, committed turn at far more g than anything with a pilot in it would pull — when a missile
 * is close enough that the turn will still be developing when the warhead arrives.
 *
 * The break is timed off **time to impact**, not range, because that is the quantity that decides
 * whether it works. `missile.test.ts` measures the window: too early and the missile has seconds
 * to re-converge, too late and the drone has not moved far enough before the warhead is level
 * with it. Both fail. The drone therefore aims for the middle of that window and misjudges it by
 * a seeded random amount, so it sometimes gets away and sometimes does not — which is the honest
 * behaviour, and much better than a drone that is either invincible or doomed.
 *
 * ## The balloons
 *
 * Tethered, bobbing, and — deliberately — with an infra-red signature of exactly zero, so no
 * seeker can ever see one. The canyon course is a *gunnery* exercise; a balloon that could be
 * locked would make it four seconds of pressing the missile button. See `weapons/seeker.ts`.
 *
 * `updateFixed(dt)`: yes — {@link TargetField.updateFixed}. Allocation-free after construction.
 */

import { Vector3 } from 'three';
import { DEG2RAD, G0, TWO_PI, clamp, clamp01 } from '../core/math.js';
import type { Rng } from '../core/rng.js';
import type { Engageable, TargetList } from '../aircraft/weapons/engagement.js';

export type TargetKind = 'racetrack' | 'evasive' | 'balloon';

/** What a drone is doing about the fact that somebody is shooting at it. */
export type DroneBehaviour = 'cruise' | 'jink' | 'break';

/**
 * One target.
 *
 * Extends {@link Engageable} — the weapons see only that half — and adds everything the mode and
 * the renderer need. All plain data; nothing here holds a reference to a mesh.
 */
export interface Target extends Omit<Engageable, 'id'> {
  /** Mutable here, read-only through {@link Engageable} — the field assigns it once at spawn. */
  id: number;
  kind: TargetKind;
  maxHitPoints: number;
  /** Position in the course's local frame [m], NED-aligned. The authoritative one. */
  readonly localNed: Vector3;
  /** Heading [rad], for pointing the model. */
  headingRad: number;
  /** Bank angle [rad] — drones roll into their turns, which is most of how a break reads. */
  bankRad: number;
  behaviour: DroneBehaviour;
  /** Arc length along the racetrack [m]. */
  pathS: number;
  /** Seconds since it died, for the debris and the chute. */
  deadForS: number;
  /** How much of its life it has left, 0…1 — the renderer smokes a damaged drone. */
  health: number;
  /** Set on the tick it dies, so the mode can score it and the effects can fire once. */
  justDied: boolean;
  /** Set on any tick it takes damage, for hit sparks. */
  justHit: boolean;
  /** Seeded per target so several drones do not jink in lockstep. */
  jinkPhase: number;
  /** Countdown to the next jink reversal [s]. */
  jinkTimerS: number;
  /** Which way the current break turns: +1 right, −1 left. */
  breakSign: number;
  /** Time-to-impact at which this drone will commit to its break [s]. */
  breakTriggerS: number;
  /** True once it has committed, so it does not re-decide every tick. */
  breakCommitted: boolean;
}

function makeTarget(): Target {
  return {
    id: -1,
    alive: false,
    positionNed: new Vector3(),
    velocityNed: new Vector3(),
    radiusM: 3,
    infraredIntensity: 0,
    hitPoints: 0,
    kind: 'balloon',
    maxHitPoints: 1,
    localNed: new Vector3(),
    headingRad: 0,
    bankRad: 0,
    behaviour: 'cruise',
    pathS: 0,
    deadForS: 0,
    health: 1,
    justDied: false,
    justHit: false,
    jinkPhase: 0,
    jinkTimerS: 0,
    breakSign: 1,
    breakTriggerS: 0.65,
    breakCommitted: false,
  };
}

/** The stadium circuit both drones fly. */
export interface RacetrackConfig {
  /** Centre of the circuit in course-local coordinates [m], NED-aligned. */
  centreNed: [number, number, number];
  /** Length of each straight [m]. */
  straightM: number;
  /** Radius of each turn [m]. */
  turnRadiusM: number;
  /** Ground speed [m/s]. */
  speedMps: number;
  /** True heading of the straights [deg]. */
  headingDeg: number;
}

export interface DroneSpec {
  kind: 'racetrack' | 'evasive';
  hitPoints: number;
  radiusM: number;
  infraredIntensity: number;
  track: RacetrackConfig;
  /** Sustained turn the drone will pull while jinking [g]. */
  jinkG: number;
  /** The one hard turn it pulls to defeat a missile [g]. */
  breakG: number;
}

export interface BalloonSpec {
  /** Position in course-local coordinates [m], NED-aligned. */
  positionNed: [number, number, number];
  hitPoints: number;
  radiusM: number;
  /** How far it rides up and down on its tether [m]. */
  bobAmplitudeM: number;
  bobPeriodS: number;
}

/** What the drones need to know about the missiles that are after them. */
export interface ThreatInput {
  /** Is a seeker locked onto this field's drones at all? */
  locked: boolean;
  /** Id of the locked target, or −1. */
  lockedTargetId: number;
  /** Is a guiding missile in the air? */
  missileInFlight: boolean;
  /** Which target it is guiding on, or −1. */
  missileTargetId: number;
  /** Where it is, sim-frame NED [m]. */
  readonly missilePositionNed: Vector3;
  readonly missileVelocityNed: Vector3;
}

export function makeThreatInput(): ThreatInput {
  return {
    locked: false,
    lockedTargetId: -1,
    missileInFlight: false,
    missileTargetId: -1,
    missilePositionNed: new Vector3(),
    missileVelocityNed: new Vector3(),
  };
}

const toMissile = /* #__PURE__ */ new Vector3();
const relativeVelocity = /* #__PURE__ */ new Vector3();

/**
 * Every target in a course, and the behaviours that move them.
 *
 * Implements {@link TargetList} directly, so the weapons see it without any adapter.
 */
export class TargetField implements TargetList {
  private readonly targets: Target[] = [];
  private readonly specs: Array<DroneSpec | BalloonSpec | null> = [];
  /** The course anchor in sim-frame NED, refreshed by the mode each tick. */
  private readonly anchorNed = new Vector3();
  private readonly rng: Rng;
  private nextId = 1;
  private elapsedS = 0;

  constructor(rng: Rng) {
    this.rng = rng;
  }

  get length(): number {
    return this.targets.length;
  }

  at(index: number): Target {
    return this.targets[index];
  }

  /** Every target, for the renderer. */
  get all(): readonly Target[] {
    return this.targets;
  }

  get aliveCount(): number {
    let alive = 0;
    for (const target of this.targets) if (target.alive) alive++;
    return alive;
  }

  /** Drop everything. */
  clear(): void {
    this.targets.length = 0;
    this.specs.length = 0;
    this.nextId = 1;
    this.elapsedS = 0;
  }

  /** Where the course sits in the world right now. Called once a tick by the mode. */
  setAnchor(north: number, east: number, down: number): void {
    this.anchorNed.set(north, east, down);
  }

  addDrone(spec: DroneSpec): Target {
    const target = makeTarget();
    target.id = this.nextId++;
    target.kind = spec.kind;
    target.alive = true;
    target.hitPoints = spec.hitPoints;
    target.maxHitPoints = spec.hitPoints;
    target.radiusM = spec.radiusM;
    target.infraredIntensity = spec.infraredIntensity;
    target.health = 1;
    target.jinkPhase = this.rng.range(0, TWO_PI);
    target.jinkTimerS = this.rng.range(0.8, 2.2);
    target.breakSign = this.rng.bool() ? 1 : -1;
    /*
     * When this drone will commit to its break, as a time to impact.
     *
     * Centred on the window `missile.test.ts` measures and spread across its edges, so the same
     * shot is not always survivable and not always fatal. A drone that always escaped would make
     * the missile pointless; one that never did would make the criterion a lie.
     */
    target.breakTriggerS = this.rng.range(0.45, 0.95);
    // Spread the drones around their circuits so they are not nose-to-tail.
    target.pathS = this.rng.range(0, perimeterOf(spec.track));
    this.targets.push(target);
    this.specs.push(spec);
    this.stepDrone(target, spec, 0);
    return target;
  }

  addBalloon(spec: BalloonSpec): Target {
    const target = makeTarget();
    target.id = this.nextId++;
    target.kind = 'balloon';
    target.alive = true;
    target.hitPoints = spec.hitPoints;
    target.maxHitPoints = spec.hitPoints;
    target.radiusM = spec.radiusM;
    // Zero, and it matters — see the module header.
    target.infraredIntensity = 0;
    target.health = 1;
    target.jinkPhase = this.rng.range(0, TWO_PI);
    target.localNed.set(spec.positionNed[0], spec.positionNed[1], spec.positionNed[2]);
    this.targets.push(target);
    this.specs.push(spec);
    this.refreshWorldPosition(target);
    return target;
  }

  /** Clear the one-tick flags. Call after the renderer and the scorer have read them. */
  clearEvents(): void {
    for (const target of this.targets) {
      target.justDied = false;
      target.justHit = false;
    }
  }

  /**
   * Apply a hit.
   *
   * The mode calls this while draining the weapons' hit queue — see `engagement.ts` on why damage
   * is a queue rather than a call from inside the projectile.
   *
   * @returns true if this hit killed it.
   */
  damage(targetId: number, amount: number): boolean {
    for (const target of this.targets) {
      if (target.id !== targetId || !target.alive) continue;
      target.hitPoints -= amount;
      target.justHit = true;
      target.health = clamp01(target.hitPoints / target.maxHitPoints);
      if (target.hitPoints <= 0) {
        target.alive = false;
        target.justDied = true;
        target.deadForS = 0;
        target.health = 0;
        return true;
      }
      return false;
    }
    return false;
  }

  /** Move everything one step. */
  updateFixed(dt: number, threat: ThreatInput): void {
    this.elapsedS += dt;
    for (let i = 0; i < this.targets.length; i++) {
      const target = this.targets[i];
      const spec = this.specs[i];
      if (spec === null) continue;

      if (!target.alive) {
        target.deadForS += dt;
        if (target.kind !== 'balloon') this.stepWreckage(target, dt);
        continue;
      }

      if (target.kind === 'balloon') {
        this.stepBalloon(target, spec as BalloonSpec);
      } else {
        const drone = spec as DroneSpec;
        if (drone.kind === 'evasive') this.decideBehaviour(target, threat, dt);
        this.stepDrone(target, drone, dt);
      }
    }
  }

  /**
   * Decide what an evasive drone is doing.
   *
   * The escalation is: nothing is after me → cruise; a seeker is on me → jink; a missile is about
   * to arrive → break, and stay broken. Committing rather than re-deciding is the point — a drone
   * that re-evaluated every tick would ease out of the turn the moment the geometry improved,
   * which is exactly how not to defeat a missile.
   */
  private decideBehaviour(target: Target, threat: ThreatInput, dt: number): void {
    if (target.breakCommitted) return;

    if (threat.missileInFlight && threat.missileTargetId === target.id) {
      toMissile.subVectors(threat.missilePositionNed, target.positionNed);
      const range = toMissile.length();
      relativeVelocity.subVectors(threat.missileVelocityNed, target.velocityNed);
      const closingSpeed = -relativeVelocity.dot(toMissile) / Math.max(1, range);
      const timeToImpact = closingSpeed > 1 ? range / closingSpeed : Number.POSITIVE_INFINITY;

      if (timeToImpact <= target.breakTriggerS) {
        target.behaviour = 'break';
        target.breakCommitted = true;
        /*
         * Break across the missile's line of sight.
         *
         * Turning to put the threat on the beam is what maximises the crossing rate the missile
         * has to null, and it is what a pilot is taught. Which way round is chosen by whichever
         * side the missile is already on, so the drone turns the short way into it.
         */
        const bearing = Math.atan2(toMissile.y, toMissile.x) - target.headingRad;
        target.breakSign = Math.sin(bearing) >= 0 ? 1 : -1;
        return;
      }
      target.behaviour = 'jink';
      this.stepJink(target, dt);
      return;
    }

    if (threat.locked && threat.lockedTargetId === target.id) {
      target.behaviour = 'jink';
      this.stepJink(target, dt);
      return;
    }
    target.behaviour = 'cruise';
  }

  /** Reverse the jink every second or two, so it is unpredictable but not a shimmy. */
  private stepJink(target: Target, dt: number): void {
    target.jinkTimerS -= dt;
    if (target.jinkTimerS <= 0) {
      target.jinkTimerS = this.rng.range(0.9, 2.1);
      target.breakSign = -target.breakSign;
    }
  }

  /**
   * Fly a drone.
   *
   * Cruise follows the racetrack by arc length. Jink and break leave the track and integrate a
   * heading directly, because a drone that snapped back onto its circuit mid-evasion would be
   * both wrong and very obvious.
   */
  private stepDrone(target: Target, spec: DroneSpec, dt: number): void {
    const track = spec.track;
    const speed = track.speedMps;

    if (target.behaviour === 'cruise') {
      target.pathS += speed * dt;
      const perimeter = perimeterOf(track);
      if (target.pathS >= perimeter) target.pathS -= perimeter;
      samplePath(track, target.pathS, target.localNed, target);
    } else {
      const g = target.behaviour === 'break' ? spec.breakG : spec.jinkG;
      // Turn rate for a level turn at `g`: ω = g·G₀/v.
      const turnRate = (g * G0) / Math.max(1, speed);
      target.headingRad += turnRate * target.breakSign * dt;
      target.localNed.x += Math.cos(target.headingRad) * speed * dt;
      target.localNed.y += Math.sin(target.headingRad) * speed * dt;
      // Bank to match the turn — `tan φ = g_lateral / 1`, capped so a break does not look absurd.
      target.bankRad = clamp(Math.atan(g) * target.breakSign, -80 * DEG2RAD, 80 * DEG2RAD);
    }

    this.refreshWorldPosition(target);
    // Velocity from the heading rather than differenced, so it is exact and allocation-free.
    target.velocityNed.set(
      Math.cos(target.headingRad) * speed,
      Math.sin(target.headingRad) * speed,
      0,
    );
  }

  /** A balloon rides its tether, and that is all it does. */
  private stepBalloon(target: Target, spec: BalloonSpec): void {
    const phase = (this.elapsedS / spec.bobPeriodS) * TWO_PI + target.jinkPhase;
    target.localNed.z = spec.positionNed[2] - Math.sin(phase) * spec.bobAmplitudeM;
    this.refreshWorldPosition(target);
    target.velocityNed.set(0, 0, 0);
  }

  /** A dead drone falls, tumbling, until it is off the field. */
  private stepWreckage(target: Target, dt: number): void {
    target.velocityNed.z += G0 * dt;
    // A lot of drag, so the wreck falls like debris rather than like a shell.
    target.velocityNed.multiplyScalar(1 - Math.min(0.9, 0.35 * dt));
    target.localNed.addScaledVector(target.velocityNed, dt);
    target.headingRad += 1.4 * dt;
    target.bankRad += 2.1 * dt;
    this.refreshWorldPosition(target);
  }

  private refreshWorldPosition(target: Target): void {
    target.positionNed.set(
      this.anchorNed.x + target.localNed.x,
      this.anchorNed.y + target.localNed.y,
      this.anchorNed.z + target.localNed.z,
    );
  }
}

/** Total length of a stadium circuit [m]. */
export function perimeterOf(track: RacetrackConfig): number {
  return 2 * track.straightM + TWO_PI * track.turnRadiusM;
}

/**
 * Position and heading at arc length `s` around the circuit.
 *
 * A stadium is four segments — straight, semicircle, straight, semicircle — and this is simply
 * which one `s` has reached. Closed-form rather than integrated so the drone is exactly periodic:
 * an integrated heading accumulates error and the circuit slowly drifts, which over a long session
 * moves a scored target off its course.
 */
export function samplePath(
  track: RacetrackConfig,
  s: number,
  outLocal: Vector3,
  outHeading: { headingRad: number; bankRad: number },
): void {
  const half = track.straightM * 0.5;
  const radius = track.turnRadiusM;
  const arc = Math.PI * radius;

  // Coordinates along (u) and across (v) the circuit, before rotating into the track's heading.
  let u = 0;
  let v = 0;
  let course = 0;
  let banked = 0;

  if (s < track.straightM) {
    u = -half + s;
    v = -radius;
    course = 0;
  } else if (s < track.straightM + arc) {
    const theta = (s - track.straightM) / radius;
    u = half + Math.sin(theta) * radius;
    v = -Math.cos(theta) * radius;
    course = theta;
    banked = 1;
  } else if (s < 2 * track.straightM + arc) {
    u = half - (s - track.straightM - arc);
    v = radius;
    course = Math.PI;
  } else {
    const theta = (s - 2 * track.straightM - arc) / radius;
    u = -half - Math.sin(theta) * radius;
    v = Math.cos(theta) * radius;
    course = Math.PI + theta;
    banked = 1;
  }

  // Rotate the circuit into its true heading. NED: x north, y east.
  const heading = track.headingDeg * DEG2RAD;
  const cos = Math.cos(heading);
  const sin = Math.sin(heading);
  outLocal.x = track.centreNed[0] + u * cos - v * sin;
  outLocal.y = track.centreNed[1] + u * sin + v * cos;
  outLocal.z = track.centreNed[2];
  outHeading.headingRad = heading + course;
  // Bank into the turns; the straights are level.
  outHeading.bankRad = banked * 25 * DEG2RAD;
}
